import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, FileText, User, Phone, AlertTriangle, Activity, Stethoscope, Heart, Pill, Printer } from 'lucide-react';
import { useAppSelector } from '../store';
import { API_BASE_URL } from '../lib/utils';
import { usePermission } from '../components/shared/PermissionGuard';
import type { PatientReportForm } from '../types/prf';

type StoredPRF = Partial<PatientReportForm> & {
  id: string;
  incidentId: string;
  eventId?: string;
  submittedAt?: string;
  createdBy?: string;
  data?: Partial<PatientReportForm>;
};

export default function PRFDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { token, user } = useAppSelector((state) => state.auth);
  const [prf, setPrf] = useState<StoredPRF | null>(null);
  const [loading, setLoading] = useState(true);

  const canView = usePermission('prfs:view');
  const canPrint = usePermission('prfs:print');

  useEffect(() => {
    if (!id || !token || !canView) {
      setLoading(false);
      return;
    }
    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/prfs/${encodeURIComponent(id)}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          setPrf(data.prf);
        }
      } catch (e) {
        console.error('Failed to fetch PRF', e);
      } finally {
        setLoading(false);
      }
    })();
  }, [id, token, canView]);

  if (!canView) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <FileText className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
          <h2 className="text-lg font-semibold text-foreground mb-1">Access Denied</h2>
          <p className="text-sm text-muted-foreground">You do not have permission to view Patient Report Forms.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-sm text-muted-foreground">Loading PRF...</p>
      </div>
    );
  }

  if (!prf) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <p className="text-sm text-muted-foreground mb-3">PRF not found</p>
          <button onClick={() => navigate(-1)} className="text-sm text-primary hover:underline">Go back</button>
        </div>
      </div>
    );
  }

  // Data may be nested under `data` (from POST) or at root
  const d = prf.data || prf;
  const patient = d.patientDetails || {};
  const nok = d.nokDetails || {};
  const complaint = d.presentingComplaint || {};
  const pmh = d.pastMedicalHistory || {};
  const allergies = d.allergiesMeds || {};
  const fast = d.fast || {};
  const obs = d.observations || {};
  const attending = d.attending || {};

  const formatDate = (iso?: string) => {
    if (!iso) return '—';
    try { return new Date(iso).toLocaleString(); } catch { return iso; }
  };

  const renderField = (label: string, value: unknown) => {
    let display: string;
    if (value === undefined || value === null || value === '') display = '—';
    else if (Array.isArray(value)) display = value.length ? value.join(', ') : '—';
    else if (typeof value === 'boolean') display = value ? 'Yes' : 'No';
    else display = String(value);
    return (
      <div className="flex flex-col gap-0.5">
        <span className="text-[8px] uppercase tracking-wider text-gray-500 font-semibold print:text-gray-600">{label}</span>
        <span className="text-sm text-gray-900 print:text-gray-800">{display}</span>
      </div>
    );
  };

  const Section = ({ title, icon: Icon, children }: { title: string; icon: typeof User; children: React.ReactNode }) => (
    <div className="bg-card border border-border rounded-lg overflow-hidden print:bg-gray-50 print:border-l-4 print:border-l-purple-600 print:border-t print:border-r print:border-b print:border-gray-300 print:rounded-none print:mb-4 print-section">
      <div className="flex items-center gap-2 px-4 py-2.5 border-b border-border bg-accent/30 print:bg-purple-600 print:text-white print:border-b-0">
        <Icon className="w-4 h-4 text-primary print:text-white" />
        <h3 className="text-sm font-semibold text-foreground print:text-white">{title}</h3>
      </div>
      <div className="p-4 grid grid-cols-2 md:grid-cols-3 gap-4 print:grid-cols-1 print:gap-3 print:p-4">{children}</div>
    </div>
  );

  return (
    <>
      <style>{`
        @media print {
          @page {
            margin: 15mm 15mm 20mm 15mm;
            size: A4;
            @top-center {
              content: "Event ID: ${prf.eventId || '—'} | Incident ID: ${prf.incidentId} | PRF ID: ${prf.id}";
              font-size: 8pt;
              color: #666;
              font-family: sans-serif;
            }
            @bottom-center {
              content: "CONFIDENTIAL | Printed by: ${user?.username || 'Unknown'}";
              font-size: 8pt;
              color: #666;
              font-family: sans-serif;
            }
          }
          .print-section {
            break-inside: avoid;
            page-break-inside: avoid;
          }
        }
      `}</style>
      <div className="flex flex-col h-full bg-background overflow-hidden print:bg-white print:font-sans print:text-gray-900">
        {/* Header */}
        <div className="flex-shrink-0 bg-card border-b border-border px-4 py-3 flex items-center gap-3 print:hidden">
          <button
            onClick={() => navigate(-1)}
            className="p-1.5 rounded-md hover:bg-accent transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          <FileText className="w-5 h-5 text-primary" />
          <div className="flex-1 min-w-0">
            <h1 className="text-base font-bold text-foreground truncate">{prf.id}</h1>
            <p className="text-xs text-muted-foreground">
              Incident: <span className="font-mono">{prf.incidentId}</span>
              {prf.submittedAt && <> &middot; Submitted: {formatDate(prf.submittedAt)}</>}
              {prf.createdBy && <> &middot; By: {prf.createdBy}</>}
            </p>
          </div>
          {canPrint && (
            <button
              onClick={() => {
                // Fire-and-forget audit log; don't block printing if it fails
                if (id && token) {
                  fetch(`${API_BASE_URL}/prfs/${id}/print`, {
                    method: 'POST',
                    headers: { Authorization: `Bearer ${token}` },
                  }).catch(() => { /* ignore */ });
                }
                window.print();
              }}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition-colors text-xs font-medium"
              title="Print PRF"
            >
              <Printer className="w-3.5 h-3.5" />
              Print
            </button>
          )}
        </div>

        {/* Print Header */}
        <div className="hidden print:flex print:flex-col print:bg-gradient-to-r print:from-purple-600 print:to-purple-800 print:text-white print:p-4 print:mb-4 print:rounded-t">
          <div className="print:flex print:justify-between print:text-xs print:opacity-90 print:mb-2">
            <span>Event ID: {prf.eventId || '—'}</span>
            <span>Incident ID: {prf.incidentId}</span>
            <span>PRF ID: {prf.id}</span>
          </div>
          <div className="print:text-lg print:font-semibold print:tracking-wider">Patient Report Form</div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4 print:p-4">
        <Section title="Patient Details" icon={User}>
          {renderField('First Name', patient.firstName)}
          {renderField('Last Name', patient.lastName)}
          {renderField('Date of Birth', patient.dateOfBirth)}
          {renderField('Age', patient.age)}
          {renderField('Gender', patient.gender)}
          {renderField('NHS Number', patient.nhsNumber)}
          {renderField('Phone', patient.phoneNumber)}
          {renderField('Address', patient.address)}
          {renderField('Postcode', patient.postcode)}
        </Section>

        <Section title="Next of Kin" icon={Phone}>
          {renderField('Name', nok.name)}
          {renderField('Relationship', nok.relationship)}
          {renderField('Phone', nok.phoneNumber)}
          {renderField('Address', nok.address)}
        </Section>

        <Section title="Presenting Complaint" icon={AlertTriangle}>
          {renderField('Chief Complaint', complaint.chiefComplaint)}
          {renderField('Onset Time', complaint.onsetTime)}
          {renderField('Duration', complaint.duration)}
          {renderField('Severity', complaint.severity)}
          {renderField('Symptoms', complaint.symptoms)}
          {renderField('Notes', complaint.additionalNotes)}
        </Section>

        <Section title="Past Medical History" icon={Heart}>
          {renderField('Conditions', pmh.conditions)}
          {renderField('Surgeries', pmh.surgeries)}
          {renderField('Hospitalisations', pmh.hospitalisations)}
          {renderField('Notes', pmh.additionalNotes)}
        </Section>

        <Section title="Allergies & Medications" icon={Pill}>
          {renderField('Allergies', allergies.allergies)}
          {renderField('Current Medications', allergies.currentMedications)}
          {renderField('Recent Medications', allergies.recentMedications)}
          {renderField('Notes', allergies.additionalNotes)}
        </Section>

        <Section title="F.A.S.T (Stroke Assessment)" icon={Activity}>
          {renderField('Face Drooping', fast.faceDropping)}
          {renderField('Arm Weakness', fast.armWeakness)}
          {renderField('Speech Difficulty', fast.speechDifficulty)}
          {renderField('Time of Onset', fast.timeOfOnset)}
          {renderField('FAST Positive', fast.fastPositive)}
          {renderField('Notes', fast.additionalNotes)}
        </Section>

        <Section title="Observations" icon={Activity}>
          {renderField('BP Systolic', obs.bloodPressureSystolic)}
          {renderField('BP Diastolic', obs.bloodPressureDiastolic)}
          {renderField('Heart Rate', obs.heartRate)}
          {renderField('Resp. Rate', obs.respiratoryRate)}
          {renderField('SpO2', obs.oxygenSaturation)}
          {renderField('Temperature', obs.temperature)}
          {renderField('Glucose', obs.glucoseLevel)}
          {renderField('Pain Score', obs.painScore)}
          {renderField('Consciousness', obs.consciousness)}
          {renderField('Time Recorded', obs.timeRecorded)}
          {renderField('Notes', obs.additionalNotes)}
        </Section>

        <Section title="Attending" icon={Stethoscope}>
          {renderField('Primary Clinician', attending.primaryClinician)}
          {renderField('Secondary Clinician', attending.secondaryClinician)}
          {renderField('Role', attending.role)}
          {renderField('Arrival Time', attending.arrivalTime)}
          {renderField('Departure Time', attending.departureTime)}
          {renderField('Transport Destination', attending.transportDestination)}
          {renderField('Handover To', attending.handoverTo)}
          {renderField('Notes', attending.additionalNotes)}
        </Section>
      </div>

      {/* Print Footer */}
      <div className="hidden print:flex print:justify-between print:items-center print:px-5 print:py-3 print:bg-gray-100 print:border-t print:border-gray-300 print:text-xs print:text-gray-600 print:mt-8">
        <span className="font-semibold text-red-600 print:text-red-600 print:uppercase print:tracking-wider">CONFIDENTIAL</span>
        <span>Printed by: {user?.username || 'Unknown'}</span>
      </div>
      </div>
    </>
  );
}
